import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Search, CloudUpload, Image, Eye, Check, Pencil, Download, Trash2, X } from 'lucide-react';
import { useAppStore } from '../../stores/appStore';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { Dialog } from '../ui/Modal/Modal';
import { Card, CardContent } from '../ui/Card/Card';
import { SimpleTooltip } from '../ui/Tooltip/Tooltip';
import { cn } from '@/lib/utils';
import { useNotificationStore } from '../../stores/notificationStore';

export function GalleryManager({ onClose, embedded = false }: { onClose: () => void; embedded?: boolean }) {
	const items = useAppStore((s) => s.galleryImages);
	const galleryTotal = useAppStore((s) => s.galleryTotal);
	const galleryHasMore = useAppStore((s) => s.galleryHasMore);
	const galleryLoadingMore = useAppStore((s) => s.galleryLoadingMore);
	const refreshGallery = useAppStore((s) => s.refreshGallery);
	const loadMoreGallery = useAppStore((s) => s.loadMoreGallery);
	const uploadGalleryImage = useAppStore((s) => s.uploadGalleryImage);
	const deleteGalleryImage = useAppStore((s) => s.deleteGalleryImage);
	const renameGalleryImage = useAppStore((s) => s.renameGalleryImage);
	const analyzeGalleryImage = useAppStore((s) => s.analyzeGalleryImage);

	const [query, setQuery] = useState('');
	const [isUploading, setUploading] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editingTitle, setEditingTitle] = useState('');
	const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
	const [manuallyAnalyzingIds, setManuallyAnalyzingIds] = useState<Set<string>>(new Set());
	const [viewingImage, setViewingImage] = useState<{ id: string; url: string; title: string } | null>(null);

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		refreshGallery();
	}, [refreshGallery]);

	// Infinite scroll observer - only activate after initial load has items
	React.useEffect(() => {
		if (!loadMoreTriggerRef.current || items.length === 0) return;
		// Delay observer setup to avoid firing on initial render
		const timeoutId = setTimeout(() => {
			if (!loadMoreTriggerRef.current) return;
			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting && galleryHasMore && !galleryLoadingMore && !query) {
						loadMoreGallery();
					}
				},
				{ root: scrollContainerRef.current, rootMargin: '100px', threshold: 0 }
			);
			observer.observe(loadMoreTriggerRef.current);
			return () => observer.disconnect();
		}, 100);
		return () => clearTimeout(timeoutId);
	}, [items.length, galleryHasMore, galleryLoadingMore, loadMoreGallery, query]);

	// Polling effect for items being analyzed (recent uploads or manual triggers)
	React.useEffect(() => {
		const itemsBeingAnalyzed = items.some(item => {
			if (item.aiAnalyzedAt) {
				// If analysis is complete, remove from manual tracking
				if (manuallyAnalyzingIds.has(item.id)) {
					setManuallyAnalyzingIds(prev => {
						const newSet = new Set(prev);
						newSet.delete(item.id);
						return newSet;
					});
				}
				return false; // Already analyzed
			}
			
			// Check if manually triggered for analysis
			if (manuallyAnalyzingIds.has(item.id)) {
				return true;
			}
			
			// Check if recently uploaded
			const uploadedAt = new Date(item.metadata.uploadedAt);
			const isRecentUpload = (Date.now() - uploadedAt.getTime()) < 10 * 60 * 1000; // 10 minutes
			return isRecentUpload;
		});
		
		if (!itemsBeingAnalyzed) {
			// No items being analyzed, no need to poll
			return;
		}

		const pollInterval = setInterval(() => {
			refreshGallery();
		}, 5000);
		
		return () => clearInterval(pollInterval);
	}, [items.map(item => `${item.id}-${!!item.aiAnalyzedAt}-${item.metadata.uploadedAt}`).join(','), manuallyAnalyzingIds.size, refreshGallery]);

	const filtered = useMemo(() => {
		if (!query.trim()) return items;
		const q = query.toLowerCase();
		return items.filter(i => (i.title || i.filename || '').toLowerCase().includes(q));
	}, [items, query]);

	const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputEl = e.currentTarget as HTMLInputElement | null;
		const list = e.target?.files ? Array.from(e.target.files) : [];
		if (list.length === 0) return;
		setUploading(true);
		try {
			let successCount = 0;
			for (const file of list) {
				try {
					await uploadGalleryImage(file, { title: file.name.replace(/\.[^.]+$/, '') });
					successCount++;
				} catch (error) {
					console.error('Failed to upload file:', file.name, error);
					useNotificationStore.getState().error(`Failed to upload ${file.name}`);
				}
			}
			await refreshGallery();
			if (successCount > 0) {
				useNotificationStore.getState().success(`Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}`);
			}
		} finally {
			setUploading(false);
			if (inputEl) {
				inputEl.value = '';
			} else {
				const el = document.getElementById('gallery-file-upload') as HTMLInputElement | null;
				if (el) el.value = '';
			}
		}
	};

	const handleDelete = async (id: string) => {
		try {
			await deleteGalleryImage(id);
			await refreshGallery();
			setDeleteConfirmId(null);
		} catch (e) {
			console.error(e);
		}
	};

	const handleDownload = async (id: string, filename: string) => {
		try {
			const orgId = localStorage.getItem('ima_active_org_id');
			const response = await fetch(`/api/gallery/${id}/download`, {
				headers: orgId ? { 'X-Organization-Id': orgId } : {},
				credentials: 'include',
			});
			if (!response.ok) throw new Error('Download failed');
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename || 'image';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Failed to download:', error);
			useNotificationStore.getState().error('Failed to download image');
		}
	};

	const beginEdit = (id: string, title: string) => {
		setEditingId(id);
		setEditingTitle(title);
	};

	const commitEdit = async () => {
		if (!editingId || !editingTitle.trim()) return;
		try {
			await renameGalleryImage(editingId, editingTitle.trim());
			await refreshGallery();
		} catch (error) {
			console.error('Failed to rename:', error);
		} finally {
			setEditingId(null);
			setEditingTitle('');
		}
	};

	const cancelEdit = () => {
		setEditingId(null);
		setEditingTitle('');
	};

	const content = (
		<div className="flex flex-col h-full">
			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
				<div className="relative flex-1 w-full">
					<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<Search className="h-5 w-5 text-gray-400" />
					</div>
					<Input
						type="text"
						placeholder="Search images..."
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						className="pl-10"
					/>
				</div>
				<input
					id="gallery-file-upload"
					type="file"
					multiple
					className="hidden"
					accept="image/*,.svg,.webp,.avif"
					onChange={handleUpload}
					disabled={isUploading}
				/>
				<Button
					variant="primary"
					size="md"
					isLoading={isUploading}
					type="button"
					onClick={() => document.getElementById('gallery-file-upload')?.click()}
					className="w-full sm:w-auto"
				>
					<CloudUpload className="w-4 h-4 mr-2" />
					{isUploading ? 'Uploading...' : 'Upload Images'}
				</Button>
			</div>
					
			<div className="flex-1 overflow-auto" ref={scrollContainerRef}>
				{filtered.length === 0 ? (
					<div className="flex items-center justify-center h-full min-h-[400px]">
						<div className="text-center space-y-4 p-8">
							<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100">
								<Image className="w-8 h-8 text-gray-400" />
							</div>
							<div>
								<h3 className="text-base font-medium text-gray-900">
									{query ? 'No images found' : 'No images yet'}
								</h3>
								<p className="mt-1 text-sm text-muted-foreground">
									{query ? 'Try a different search term' : 'Upload your first image to get started'}
								</p>
							</div>
							{!query && (
								<Button
									variant="primary"
									size="md"
									onClick={() => document.getElementById('gallery-file-upload')?.click()}
								>
									<CloudUpload className="w-4 h-4 mr-2" />
									Upload Images
								</Button>
							)}
						</div>
					</div>
				) : (
					<>
					<div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
						{filtered.map((img) => (
							<Card
							key={img.id}
							variant="default"
							padding="none"
							className="group hover:shadow-md transition-all duration-200"
							>
								<button
									type="button"
									className="aspect-square bg-gray-50 relative overflow-hidden w-full cursor-pointer"
									onClick={() => setViewingImage({ id: img.id, url: img.url, title: img.title || img.filename })}
								>
									<img
										src={img.thumbnail || img.url}
										alt={img.title || img.filename}
										className="w-full h-full object-cover"
										loading="lazy"
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
										<Eye className="w-8 h-8 text-white opacity-80" />
									</div>
								</button>
								<CardContent className="p-3">
									{editingId === img.id ? (
										<form onSubmit={(e) => { e.preventDefault(); commitEdit(); }} className="flex items-center gap-2">
											<Input 
												value={editingTitle} 
												onChange={(e) => setEditingTitle(e.target.value)}
												className="h-8 text-sm"
												autoFocus
												onBlur={cancelEdit}
												onKeyDown={(e) => {
													if (e.key === 'Escape') {
														cancelEdit();
													}
												}}
											/>
											<Button 
												type="button"
												size="sm" 
												variant="primary"
												className="h-8 px-3"
												onMouseDown={(e) => e.preventDefault()} // Prevent input from losing focus
												onClick={commitEdit}
											>
												<Check className="w-4 h-4" />
											</Button>
										</form>
									) : (
										<>
											<div className="mb-2">
												<SimpleTooltip content={img.title || img.filename}>
													<h4 className="text-sm font-medium text-gray-900 truncate cursor-help">
														{img.title || img.filename}
													</h4>
												</SimpleTooltip>
												{/* AI Analysis Status */}
												{!img.aiAnalyzedAt ? (
													(() => {
														// Check if manually triggered for analysis
														const isManuallyAnalyzing = manuallyAnalyzingIds.has(img.id);
														
														// Check if image was uploaded recently (within last 10 minutes)
														const uploadedAt = new Date(img.metadata.uploadedAt);
														const isRecentUpload = (Date.now() - uploadedAt.getTime()) < 10 * 60 * 1000;
														
														if (isManuallyAnalyzing || isRecentUpload) {
															return (
																<div className="flex items-center gap-1 mt-1">
																	<div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
																	<span className="text-xs text-blue-600">Analyzing...</span>
																</div>
															);
														} else {
															return (
																<button 
																	onClick={async () => {
																		// Add to manually analyzing set
																		setManuallyAnalyzingIds(prev => new Set(prev).add(img.id));
																		try {
																			await analyzeGalleryImage(img.id);
																		} catch (error) {
																			// Remove from set if failed
																			setManuallyAnalyzingIds(prev => {
																				const newSet = new Set(prev);
																				newSet.delete(img.id);
																				return newSet;
																			});
																		}
																	}}
																	className="text-xs text-blue-600 hover:text-blue-700 underline mt-1"
																	title="Click to analyze with AI"
																>
																	🤖 Analyze with AI
																</button>
															);
														}
													})()
												) : img.description ? (
													<SimpleTooltip content={img.description}>
														<p className="text-xs text-gray-600 truncate mt-1 cursor-help">
															📝 {img.description}
														</p>
													</SimpleTooltip>
												) : (
													<p className="text-xs text-gray-400 mt-1">
														✅ Analyzed
													</p>
												)}
											</div>
											<div className="flex items-center gap-1">
												<Button
													size="sm"
													variant="ghost"
													onClick={() => beginEdit(img.id, img.title || img.filename)}
													className="h-8 px-2 flex-1"
													title="Rename image"
												>
													<Pencil className="w-4 h-4" />
													<span className="ml-1.5">Rename</span>
												</Button>
												<Button
													size="sm"
													variant="ghost"
													onClick={() => handleDownload(img.id, img.title || img.filename)}
													className="h-8 px-2 flex-shrink-0"
													title="Download image"
												>
													<Download className="w-4 h-4" />
												</Button>
												<Button
													size="sm"
													variant="ghost"
													onClick={() => setDeleteConfirmId(img.id)}
													className="h-8 px-2 !text-red-600 hover:!text-red-700 hover:!bg-red-50 flex-shrink-0"
													title="Delete image"
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</div>
										</>
									)}
								</CardContent>
							</Card>
						))}
					</div>
					{/* Infinite scroll trigger */}
					{!query && galleryHasMore && (
						<div ref={loadMoreTriggerRef} className="flex justify-center py-6">
							{galleryLoadingMore ? (
								<div className="flex items-center gap-2 text-gray-500">
									<div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
									<span className="text-sm">Loading more images...</span>
								</div>
							) : (
								<span className="text-sm text-gray-400">Scroll for more</span>
							)}
						</div>
					)}
					{/* Show total count */}
					{galleryTotal > 0 && (
						<div className="text-center text-sm text-gray-500 py-2">
							Showing {filtered.length} of {galleryTotal} images
						</div>
					)}
					</>
				)}
			</div>
		</div>
	);

	return (
		<>
			{embedded ? content : (
				<Modal
					isOpen={true}
					onClose={onClose}
					title="Gallery Manager"
					className="max-w-5xl"
				>
					{content}
				</Modal>
			)}

			<Dialog
				isOpen={!!deleteConfirmId}
				onClose={() => setDeleteConfirmId(null)}
				title="Delete Gallery Item"
				description="Are you sure you want to delete this item? This action cannot be undone."
				confirmText="Delete"
				confirmVariant="destructive"
				onConfirm={() => deleteConfirmId && handleDelete(deleteConfirmId)}
			/>

			{/* Image Viewer Modal */}
			{viewingImage && (
				<div
					className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
					onClick={() => setViewingImage(null)}
				>
					<button
						type="button"
						className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
						onClick={() => setViewingImage(null)}
					>
						<X className="w-8 h-8" />
					</button>
					<div className="absolute top-4 left-4 text-white text-lg font-medium max-w-[60%] truncate">
						{viewingImage.title}
					</div>
					<button
						type="button"
						className="absolute top-4 right-16 text-white hover:text-gray-300 transition-colors"
						title="Download"
						onClick={(e) => {
							e.stopPropagation();
							handleDownload(viewingImage.id, viewingImage.title);
						}}
					>
						<Download className="w-7 h-7" />
					</button>
					<img
						src={viewingImage.url}
						alt={viewingImage.title}
						className="max-w-[90vw] max-h-[90vh] object-contain"
						onClick={(e) => e.stopPropagation()}
					/>
				</div>
			)}
		</>
	);
}